import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  inject
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  CoreActionFormOpenExternalTarget,
  CoreActionsTypes,
  selectorCoreDeeplinkFormData,
  selectorCoreLogin,
  selectorCoreLoadedFormData,
  CoreUtilsService,
  CoreExternalAppService,
  ENVIRONMENT
} from '@bepatient/core';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { ButtonQuestionV3 } from '../../../classes/question-button';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';
import { Subscription } from 'rxjs';
import { take, filter } from 'rxjs/operators';
import { Platform } from '@ionic/angular';

@Component({
  selector: 'bepatient-input-button-v3',
  templateUrl: './input-button.component.html',
  styleUrls: ['./input-button.component.scss'],
  standalone: false
})
export class InputButtonComponentV3
  extends QuestionBaseV3ComponentDirective
  implements AfterViewInit, OnDestroy
{
  private store$ = inject<Store<any>>(Store);
  private coreUtilsService = inject(CoreUtilsService);
  private coreExternalAppService = inject(CoreExternalAppService);
  private platform = inject(Platform);
  private actions$ = inject(Actions);
  private environment = inject(ENVIRONMENT);

  @Input() formGroup: any;
  @Input() question: ButtonQuestionV3;
  @Input() color: string;
  safeHtml: SafeHtml;
  subscription: Subscription;
  isCompleted = false;
  isFailed = false;

  ngAfterViewInit(): void {
    this.subscription = this.store$
      .select(selectorCoreDeeplinkFormData)
      .subscribe(res => {
        if (res && res.fieldId && res.fileName) {
          this.formGroup?.get(res.fieldId)?.setValue(res.fileName);
        }
      });

    // Check if assessment is already completed with a delay to ensure form ID is set
    setTimeout(() => {
      this.checkExternalAppCompletionStatus();
    }, 100);

    // Listen for app resume to check external app completion status
    if (this.platform.is('hybrid') || this.platform.is('mobile')) {
      this.platform.resume.subscribe(() => {
        // Re-check external app completion status when app resumes
        setTimeout(() => {
          this.checkExternalAppCompletionStatus();
        }, 500); // Slightly longer delay to ensure localStorage is updated
      });
    }

    // Listen for external app completion status updates from browser close detection
    if (
      this.question?.target &&
      this.coreExternalAppService.isTargetAllowed(this.question.target)
    ) {
      this.actions$
        .pipe(
          ofType(CoreActionsTypes.SAVE_DEEPLINK_FORM_DATA),
          filter(
            (action: any) =>
              action.payload?.uniqueKey === this.getUniqueButtonKey()
          )
        )
        .subscribe(() => {
          this.checkExternalAppCompletionStatus();
        });
    }
  }

  // Check if assessment is completed
  private checkExternalAppCompletionStatus(): void {
    if (
      this.question?.target &&
      this.coreExternalAppService.isTargetAllowed(this.question.target)
    ) {
      try {
        const uniqueKey = this.getUniqueButtonKey();
        const deeplinkFormData = this.getDeeplinkFormCompletionData();

        // TODO: Call cleanupOldLocalStorageData() when appropriate (e.g., on app startup, user logout, etc.)
        // this.cleanupOldLocalStorageData();

        if (deeplinkFormData && deeplinkFormData[uniqueKey]) {
          const completionData = deeplinkFormData[uniqueKey];
          this.isCompleted = completionData.completed === true;
          this.isFailed =
            completionData.status === 'failed' ||
            completionData.status === 'error';
        }
      } catch (error) {
        console.error('Error checking assessment completion status:', error);
      }
    }
  }

  // Clean up old localStorage data that doesn't match current format
  private cleanupOldLocalStorageData(): void {
    // Disabled for now - keeping completion data
    return;
  }

  // Get deeplink form completion data from localStorage
  private getDeeplinkFormCompletionData(): any {
    try {
      const data = localStorage.getItem('deeplink-form-completions');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error getting deeplink form completion data:', error);
      return {};
    }
  }

  // Generate unique key for this button using dynamic userId format
  private getUniqueButtonKey(): string {
    const fieldId = this.question?.key || 'unknown';
    const userId = this.getCurrentUserId();

    // Get form data synchronously for formId and occurrence
    let formId = 'unknown';
    let occurrence = '0';

    // Get form data from store synchronously
    this.store$
      .select(selectorCoreLoadedFormData)
      .pipe(take(1))
      .subscribe(formData => {
        if (formData) {
          formId = formData.uuid || 'unknown';
          occurrence = formData.intPageOccurrence?.toString() || '0';
        }
      });

    // Use core-utils method to build dynamic userId
    const dynamicUserId = this.coreUtilsService.buildDynamicUserIdSync(
      {
        formId: formId,
        occurrence: occurrence
      },
      parseInt(userId),
      this.environment
    );

    const uniqueKey = `${dynamicUserId}_${fieldId}`;
    return uniqueKey;
  }

  // Get current user ID from JWT token
  private getCurrentUserId(): string {
    let userId = 'unknown';
    this.store$
      .select(selectorCoreLogin)
      .pipe(take(1))
      .subscribe(loginData => {
        if (loginData && loginData.iAuth) {
          const extractedUserId = this.coreUtilsService.extractUserIdFromToken(
            loginData.iAuth
          );
          if (extractedUserId !== null) {
            userId = extractedUserId.toString();
          }
        }
      });
    return userId;
  }

  // Get current form ID from form data
  private getCurrentFormId(): string {
    let formId = 'unknown';
    this.store$
      .select(selectorCoreLoadedFormData)
      .pipe(take(1))
      .subscribe(formData => {
        if (formData && formData.uuid) {
          formId = formData.uuid;
        }
      });
    return formId;
  }

  // Get current occurrence number
  private getCurrentOccurrence(): string {
    let occurrence = '0';
    this.store$
      .select(selectorCoreLoadedFormData)
      .pipe(take(1))
      .subscribe(formData => {
        if (formData && formData.occurrence) {
          occurrence = formData.occurrence.toString();
        }
      });
    return occurrence;
  }

  async openDeeplink(event) {
    // Button clicked

    // Don't allow clicking if already completed or failed
    if (this.isCompleted || this.isFailed) {
      return;
    }

    if (event && this.question?.target && this.question?.href) {
      this.store$.dispatch(
        new CoreActionFormOpenExternalTarget({
          target: this.question?.target,
          href: this.question?.href,
          label: this.question?.label || 'Assessment',
          uniqueKey: this.getUniqueButtonKey()
        })
      );
    }
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    // Check if this is an external app button and mark as completed if user navigates away
    if (this.question?.target && !this.isCompleted) {
      // Validate target is allowed
      if (this.coreExternalAppService.isTargetAllowed(this.question.target)) {
        this.handleExternalAppNavigationAway();
      } else {
        console.warn(
          `Target '${this.question.target}' is not allowed in input button`
        );
      }
    }
  }

  /**
   * Handle external app assessment when user navigates away from the form
   * This assumes the assessment was completed if no deeplink callback was received
   */
  private handleExternalAppNavigationAway(): void {
    try {
      const uniqueKey = this.getUniqueButtonKey();
      const pendingAssessments =
        this.coreExternalAppService.getPendingExternalAppAssessments();

      if (pendingAssessments[uniqueKey]) {
        // Mark as completed
        const completionData = {
          completed: true,
          completedAt: new Date().toISOString(),
          method: 'navigation-away',
          uniqueKey: uniqueKey
        };

        // Store in deeplink-form-completions
        const existingData = this.getDeeplinkFormCompletionData();
        existingData[uniqueKey] = completionData;
        localStorage.setItem(
          'deeplink-form-completions',
          JSON.stringify(existingData)
        );

        // Remove from pending assessments
        this.coreExternalAppService.removePendingExternalAppAssessment(
          uniqueKey
        );
      }
    } catch (error) {
      console.error('Error handling external app navigation away:', error);
    }
  }
}
